// Customer portal: browse + send care packages without leaving the portal.
// Mirrors the public /packages gallery but inside PortalShell, with a
// one-click send (recipient picker) per box. Each box is merchant-anchored,
// so sending only needs a recipient.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Gift, Store, MapPin, ArrowRight, Loader2, Send, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState } from '../../components/shared/primitives';
import { Modal } from '../../components/shared/Modal';
import { Button } from '../../components/stripe-ui/Button';
import { CUSTOMER_NAV } from '../_navs';
import { useCustomerNav } from '../../lib/pendingAction';
import { carePackages, recipients, type CarePackageSummary } from '../../lib/api';
import { iconFor } from '../../lib/icons';
import { formatHtgMinor, formatDisplayFromHtgMinor, deliveryEtaLabel } from '../../lib/money';

export function CustomerCarePackagesPage() {
  const q = useQuery({ queryKey: ['storefront', 'packages'], queryFn: carePackages.list, staleTime: 5 * 60 * 1000 });
  const [sending, setSending] = useState<CarePackageSummary | null>(null);

  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen.
  const nav = useCustomerNav(CUSTOMER_NAV);

  return (
    <PortalShell
      nav={nav}
      activeId="packages"
      title="Care packages"
      subtitle="Ready-made boxes from merchants across Haiti. Pick one, choose who it's for, done."
    >
      {q.isLoading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-52 rounded-xl border border-[var(--color-hairline)] bg-white animate-pulse" />)}
        </div>
      ) : q.error ? (
        <Card><div className="text-center py-12 text-[13px] text-[var(--color-secondary-2)] inline-flex items-center gap-2 w-full justify-center"><Loader2 className="w-4 h-4" /> Couldn't load care packages.</div></Card>
      ) : (q.data ?? []).length === 0 ? (
        <Card><EmptyState icon={Gift} title="No care packages yet" body="Check back soon — merchants are still curating boxes." /></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(q.data ?? []).map((p) => <BoxCard key={p.id} pkg={p} onSend={() => setSending(p)} />)}
        </div>
      )}

      {sending && <SendBoxModal pkg={sending} onClose={() => setSending(null)} />}
    </PortalShell>
  );
}

function BoxCard({ pkg, onSend }: { pkg: CarePackageSummary; onSend: () => void }) {
  const Icon = iconFor(pkg.system_icon);
  const eta = deliveryEtaLabel(pkg.merchant?.avg_fulfillment_hours);
  return (
    <Card className="flex flex-col">
      <div className="flex items-start justify-between">
        <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-50 to-[var(--color-warning-bg)]/50 text-brand-600"><Icon className="w-5 h-5" /></span>
        {eta && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[var(--color-success-bg)] text-[var(--color-success-fg)]">{eta.label}</span>}
      </div>
      <h3 className="mt-3 text-[15px] font-semibold text-[var(--color-ink)]">{pkg.name}</h3>
      {pkg.subtitle && <p className="mt-0.5 text-[12px] text-[var(--color-secondary-2)] leading-relaxed line-clamp-2">{pkg.subtitle}</p>}
      {pkg.merchant && (
        <p className="mt-2 text-[11.5px] text-[var(--color-secondary-2)] inline-flex items-center gap-1">
          <Store className="w-3 h-3 text-[var(--color-muted)]" /> {pkg.merchant.business_name}
          {pkg.merchant.city && <><span className="text-[var(--color-muted)]">·</span><MapPin className="w-3 h-3 text-[var(--color-muted)]" /> {pkg.merchant.city.name}</>}
        </p>
      )}
      <div className="mt-3 flex items-baseline justify-between">
        <span className="text-[15px] font-mono font-semibold text-[var(--color-ink)]">{pkg.approx_htg_minor != null ? formatHtgMinor(pkg.approx_htg_minor) : '—'}</span>
        <span className="text-[11px] text-[var(--color-muted)]">{pkg.approx_htg_minor != null ? `≈ ${formatDisplayFromHtgMinor(pkg.approx_htg_minor)} · ` : ''}{pkg.items_count} items</span>
      </div>
      <div className="mt-auto pt-4 flex items-center gap-2">
        <Link to={`/packages/${pkg.slug}`} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border border-[var(--color-border)] text-[var(--color-body)] text-[12px] font-semibold hover:bg-[var(--color-panel)]">
          View items
        </Link>
        <Button onClick={onSend} className="flex-1" leadingIcon={<Send className="w-3.5 h-3.5" />}>Send</Button>
      </div>
    </Card>
  );
}

function SendBoxModal({ pkg, onClose }: { pkg: CarePackageSummary; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const recipQ = useQuery({ queryKey: ['me', 'recipients'], queryFn: recipients.list });

  const [recipientId, setRecipientId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (recipientId === '' && recipQ.data && recipQ.data.length > 0) setRecipientId(recipQ.data[0]!.id);
  }, [recipQ.data, recipientId]);

  const send = useMutation({
    mutationFn: () => {
      if (!recipientId) throw new Error('Pick a recipient.');
      return carePackages.createRequest({ package_slug: pkg.slug, recipient_id: Number(recipientId), notes: notes.trim() || null }, { submit: true });
    },
    onSuccess: (req) => {
      qc.invalidateQueries({ queryKey: ['me', 'requests'] });
      toast.success(`Package sent — ${req.cart_item_count} items, ${formatHtgMinor(req.cart_subtotal_htg_minor)}`);
      onClose();
      navigate(`/customer/requests/${req.id}`);
    },
    onError: (e: Error) => toast.error(e.message || 'Could not send the package.'),
  });

  const noRecipients = recipQ.data && recipQ.data.length === 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Send "${pkg.name}"`}
      subtitle={pkg.merchant ? `Fulfilled by ${pkg.merchant.business_name}${pkg.merchant.city ? ` · ${pkg.merchant.city.name}` : ''}` : undefined}
      footer={
        <>
          <Button tone="secondary" onClick={onClose} disabled={send.isPending}>Cancel</Button>
          <Button onClick={() => send.mutate()} loading={send.isPending} disabled={!recipientId || send.isPending}>Send &amp; submit</Button>
        </>
      }
    >
      {noRecipients ? (
        <div className="text-center py-6">
          <p className="text-[13.5px] text-[var(--color-body)] font-medium">You have no recipients yet.</p>
          <p className="mt-1 text-[12.5px] text-[var(--color-secondary-2)]">Add a family member first, then send them a package.</p>
          <Link to="/customer/recipients" className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-600 hover:underline">
            Add a recipient <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)] inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> For</span>
            <select
              value={recipientId === '' ? '' : String(recipientId)}
              onChange={(e) => setRecipientId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={recipQ.isLoading || (recipQ.data ?? []).length === 0}
              className="mt-1 w-full px-3 py-2 text-[13px] bg-white border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 disabled:bg-[var(--color-panel)] disabled:text-[var(--color-muted)]"
            >
              {(recipQ.data ?? []).length === 0
                ? <option value="">No recipients</option>
                : (recipQ.data ?? []).map((r) => <option key={r.id} value={r.id}>{r.full_name}{r.city?.name ? ` · ${r.city.name}` : ''}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">Note for the merchant (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full px-2.5 py-2 text-[13px] bg-white border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400"
              placeholder="Brand preferences, delivery timing, etc."
            />
          </label>
          <p className="text-[11.5px] text-[var(--color-muted)] leading-relaxed">
            All {pkg.items_count} items come from {pkg.merchant?.business_name ?? 'this merchant'}'s in-stock catalog at their listed prices. You'll review the USD quote before paying.
          </p>
        </div>
      )}
    </Modal>
  );
}
